// Trilingual string catalog for the Asmaluz storefront (CONTENT-003).
//
// EN is authoritative; ES is translated; PT-BR is a DRAFT pending native review
// (OI-013), consistent with the CONTENT-002 content specs. Compliance language
// is preserved in every locale: "supports / apoya / apoia", never "treats /
// cures / prevents", and the FDA disclaimer travels with every claim.

export type Locale = 'en' | 'es' | 'pt';
export const LOCALES: readonly Locale[] = ['en', 'es', 'pt'];
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
};

export type Dict = Record<string, string>;

const en: Dict = {
  'lang.label': 'Language',
  'common.fda':
    'These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
  'common.shop': 'Shop Asmaluz',

  'head.home.title': 'Asmaluz — Breathe easier. Live fuller.',
  'head.home.desc':
    'A naturally-sourced supplement that supports respiratory, immune, and lung health.',

  'home.hero.title': 'Breathe easier. Live fuller.',
  'home.hero.sub':
    'Asmaluz™ is a naturally-sourced supplement that supports respiratory, immune, and lung health* — science and nature, working together for the way you want to live.',
  'home.hero.science': 'See the science',

  'home.trust.polyphenols': 'Natural isolated polyphenols',
  'home.trust.cznc': 'Vitamin C + Zinc',
  'home.trust.vegan': 'Vegan-friendly',
  'home.trust.gluten': 'Gluten-free',
  'home.trust.gmp': 'GMP',
  'home.trust.usa': 'Made in USA',

  'home.science.title': 'Bioactive polyphenols, studied for decades.',
  'home.science.lede':
    'Asmaluz is built around isolated polyphenols from the Brazilian fern Dicksonia sellowiana — a medicinal plant approved by ANVISA — reinforced with Vitamin C and Zinc.',
  'home.proof.decades.k': 'Decades',
  'home.proof.decades.v': 'of research on the active polyphenols.',
  'home.proof.anvisa.v': 'DCB 11721 · RDC 249 · approved medicinal plant.',
  'home.proof.czn.v': 'Vitamin C and Zinc support the immune system.*',

  'home.line.title': 'Two ways to breathe easier.',
  'home.line.liquid.form': 'Liquid · 195 ml',
  'home.line.liquid.claim':
    'Our Vitamin-C-forward daily liquid. Supports respiratory & lung health.*',
  'home.line.tablets.name': 'Asmaluz™ Tablets',
  'home.line.tablets.form': 'Tablets · 30 ct',
  'home.line.tablets.claim': 'The same polyphenol science, in a convenient tablet.*',
  'home.line.view': 'View',
  'common.sample': 'sample',

  'home.story.q': 'Born from a son’s need to help his father breathe.',
  'home.story.body':
    'Asmaluz C+ was created by QFB Elzo Ferreira, who grew up watching his father struggle through asthma attacks and set out to understand what drives them. His search across Brazil for anti-inflammatory medicinal plants led to Dicksonia sellowiana and its polyphenols — and to Asmaluz.',
  'home.story.who': 'The Asmaluz story',

  'home.faq.title': 'Good to know',
  'home.faq.q1': 'Is Asmaluz safe for daily use?',
  'home.faq.a1':
    'Yes — when used as directed, Asmaluz is intended for daily use. It contains no stimulants, ephedra, or steroids. Consult your doctor before combining it with any treatment plan.',
  'home.faq.q2': 'Is it suitable for children?',
  'home.faq.a2':
    'There is a children’s dose. Consult a pediatric specialist before giving it to a child.',
  'home.faq.q3': 'How does Asmaluz work?',
  'home.faq.a3':
    'Its bioactive polyphenols — supported by antioxidant and anti-inflammatory research — act as part of a daily routine to support respiratory and immune wellness.*',

  'home.cta.title': 'Science and nature, at the service of your health.',
  'home.cta.sub': 'Choose the Asmaluz that fits your routine, and enjoy the moment of living.',

  'compliance.extra':
    'Asmaluz is a dietary supplement. Consult your healthcare provider before use, especially if you are pregnant, nursing, taking medication, or have a medical condition.',
  'compliance.made': 'Made in USA · Manufactured by Tisch Pharma Labs LLC (Miami, FL).',
};

const es: Dict = {
  'lang.label': 'Idioma',
  'common.fda':
    'Estas declaraciones no han sido evaluadas por la FDA. Este producto no está destinado a diagnosticar, tratar, curar ni prevenir ninguna enfermedad.',
  'common.shop': 'Comprar Asmaluz',

  'head.home.title': 'Asmaluz — Respira mejor. Vive más pleno.',
  'head.home.desc':
    'Un suplemento de origen natural que apoya la salud respiratoria, inmunológica y pulmonar.',

  'home.hero.title': 'Respira mejor. Vive más pleno.',
  'home.hero.sub':
    'Asmaluz™ es un suplemento de origen natural que apoya la salud respiratoria, inmunológica y pulmonar* — ciencia y naturaleza, trabajando juntas para la vida que quieres vivir.',
  'home.hero.science': 'Ver la ciencia',

  'home.trust.polyphenols': 'Polifenoles naturales aislados',
  'home.trust.cznc': 'Vitamina C + Zinc',
  'home.trust.vegan': 'Apto para veganos',
  'home.trust.gluten': 'Sin gluten',
  'home.trust.gmp': 'GMP',
  'home.trust.usa': 'Hecho en EE. UU.',

  'home.science.title': 'Polifenoles bioactivos, estudiados durante décadas.',
  'home.science.lede':
    'Asmaluz se basa en polifenoles aislados del helecho brasileño Dicksonia sellowiana — una planta medicinal aprobada por ANVISA — reforzados con Vitamina C y Zinc.',
  'home.proof.decades.k': 'Décadas',
  'home.proof.decades.v': 'de investigación sobre los polifenoles activos.',
  'home.proof.anvisa.v': 'DCB 11721 · RDC 249 · planta medicinal aprobada.',
  'home.proof.czn.v': 'La Vitamina C y el Zinc apoyan el sistema inmunológico.*',

  'home.line.title': 'Dos formas de respirar mejor.',
  'home.line.liquid.form': 'Líquido · 195 ml',
  'home.line.liquid.claim':
    'Nuestro líquido diario rico en Vitamina C. Apoya la salud respiratoria y pulmonar.*',
  'home.line.tablets.name': 'Asmaluz™ Tabletas',
  'home.line.tablets.form': 'Tabletas · 30 u',
  'home.line.tablets.claim': 'La misma ciencia de polifenoles, en una tableta práctica.*',
  'home.line.view': 'Ver',
  'common.sample': 'muestra',

  'home.story.q': 'Nació de la necesidad de un hijo de ayudar a su padre a respirar.',
  'home.story.body':
    'Asmaluz C+ fue creado por el QFB Elzo Ferreira, quien creció viendo a su padre luchar contra los ataques de asma y se propuso entender qué los provoca. Su búsqueda por todo Brasil de plantas medicinales antiinflamatorias lo llevó a Dicksonia sellowiana y sus polifenoles — y a Asmaluz.',
  'home.story.who': 'La historia de Asmaluz',

  'home.faq.title': 'Bueno saberlo',
  'home.faq.q1': '¿Es seguro usar Asmaluz a diario?',
  'home.faq.a1':
    'Sí — usado según las indicaciones, Asmaluz está pensado para uso diario. No contiene estimulantes, efedra ni esteroides. Consulta a tu médico antes de combinarlo con cualquier tratamiento.',
  'home.faq.q2': '¿Es adecuado para niños?',
  'home.faq.a2':
    'Existe una dosis infantil. Consulta a un especialista pediátrico antes de dárselo a un niño.',
  'home.faq.q3': '¿Cómo funciona Asmaluz?',
  'home.faq.a3':
    'Sus polifenoles bioactivos — respaldados por investigación antioxidante y antiinflamatoria — actúan como parte de una rutina diaria para apoyar el bienestar respiratorio e inmunológico.*',

  'home.cta.title': 'La ciencia y la naturaleza al servicio de tu salud.',
  'home.cta.sub':
    'Elige el Asmaluz que se adapta a tu rutina y disfruta el momento de vivir.',

  'compliance.extra':
    'Asmaluz es un suplemento dietético. Consulta a tu profesional de salud antes de usarlo, especialmente si estás embarazada, en período de lactancia, tomando medicación o tienes alguna condición médica.',
  'compliance.made': 'Hecho en EE. UU. · Fabricado por Tisch Pharma Labs LLC (Miami, FL).',
};

// PT-BR — DRAFT, needs native review (OI-013).
const pt: Dict = {
  'lang.label': 'Idioma',
  'common.fda':
    'Estas declarações não foram avaliadas pela FDA. Este produto não se destina a diagnosticar, tratar, curar ou prevenir qualquer doença.',
  'common.shop': 'Comprar Asmaluz',

  'head.home.title': 'Asmaluz — Respire melhor. Viva mais pleno.',
  'head.home.desc':
    'Um suplemento de origem natural que apoia a saúde respiratória, imunológica e pulmonar.',

  'home.hero.title': 'Respire melhor. Viva mais pleno.',
  'home.hero.sub':
    'Asmaluz™ é um suplemento de origem natural que apoia a saúde respiratória, imunológica e pulmonar* — ciência e natureza trabalhando juntas para a vida que você quer viver.',
  'home.hero.science': 'Ver a ciência',

  'home.trust.polyphenols': 'Polifenóis naturais isolados',
  'home.trust.cznc': 'Vitamina C + Zinco',
  'home.trust.vegan': 'Adequado para veganos',
  'home.trust.gluten': 'Sem glúten',
  'home.trust.gmp': 'GMP',
  'home.trust.usa': 'Fabricado nos EUA',

  'home.science.title': 'Polifenóis bioativos, estudados há décadas.',
  'home.science.lede':
    'A Asmaluz é baseada em polifenóis isolados da samambaia brasileira Dicksonia sellowiana — uma planta medicinal aprovada pela ANVISA — reforçados com Vitamina C e Zinco.',
  'home.proof.decades.k': 'Décadas',
  'home.proof.decades.v': 'de pesquisa sobre os polifenóis ativos.',
  'home.proof.anvisa.v': 'DCB 11721 · RDC 249 · planta medicinal aprovada.',
  'home.proof.czn.v': 'A Vitamina C e o Zinco apoiam o sistema imunológico.*',

  'home.line.title': 'Duas formas de respirar melhor.',
  'home.line.liquid.form': 'Líquido · 195 ml',
  'home.line.liquid.claim':
    'Nosso líquido diário rico em Vitamina C. Apoia a saúde respiratória e pulmonar.*',
  'home.line.tablets.name': 'Asmaluz™ Comprimidos',
  'home.line.tablets.form': 'Comprimidos · 30 un',
  'home.line.tablets.claim': 'A mesma ciência dos polifenóis, em um comprimido prático.*',
  'home.line.view': 'Ver',
  'common.sample': 'amostra',

  'home.story.q': 'Nasceu da necessidade de um filho de ajudar o pai a respirar.',
  'home.story.body':
    'A Asmaluz C+ foi criada pelo QFB Elzo Ferreira, que cresceu vendo o pai enfrentar crises de asma e decidiu entender o que as provoca. Sua busca por todo o Brasil por plantas medicinais anti-inflamatórias o levou à Dicksonia sellowiana e seus polifenóis — e à Asmaluz.',
  'home.story.who': 'A história da Asmaluz',

  'home.faq.title': 'Bom saber',
  'home.faq.q1': 'A Asmaluz é segura para uso diário?',
  'home.faq.a1':
    'Sim — quando usada conforme as instruções, a Asmaluz é indicada para uso diário. Não contém estimulantes, efedra ou esteroides. Consulte seu médico antes de combiná-la com qualquer tratamento.',
  'home.faq.q2': 'É adequada para crianças?',
  'home.faq.a2':
    'Existe uma dose infantil. Consulte um especialista pediátrico antes de dar a uma criança.',
  'home.faq.q3': 'Como a Asmaluz funciona?',
  'home.faq.a3':
    'Seus polifenóis bioativos — apoiados por pesquisas antioxidantes e anti-inflamatórias — atuam como parte de uma rotina diária para apoiar o bem-estar respiratório e imunológico.*',

  'home.cta.title': 'A ciência e a natureza a serviço da sua saúde.',
  'home.cta.sub':
    'Escolha a Asmaluz que combina com sua rotina e aproveite o momento de viver.',

  'compliance.extra':
    'A Asmaluz é um suplemento alimentar. Consulte seu profissional de saúde antes de usar, especialmente se estiver grávida, amamentando, tomando medicação ou tiver alguma condição médica.',
  'compliance.made': 'Fabricado nos EUA · Fabricado pela Tisch Pharma Labs LLC (Miami, FL).',
};

export const CATALOG: Record<Locale, Dict> = { en, es, pt };
